import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import './BottomNav.css';

/* ============================================================
   Meniscus Dock — alt sekme çubuğu
   ------------------------------------------------------------
   Aktif sekme kaymaz; ÇUBUK aktif sekmenin etrafında erir.
   Üst kenar, daireyi (bead) saran bir yuvaya dönüşür. Omuz
   yayları hem üst kenara hem daireye TEĞET olacak biçimde
   hesaplanır, böylece birleşim yerinde köşe oluşmaz:

     reach = √( (s+rb)² − (s−rise)² )

   s    : omuz yayı yarıçapı
   rb   : dairenin yarıçapı
   rise : dairenin merkezinin üst kenardan yüksekliği
   reach: daire merkezi ile omuzun üst kenara değdiği nokta
          arasındaki yatay mesafe

   Sürüklerken omuzlar hıza göre yatar (arkada kalan omuz uzar,
   öndeki toplanır) — yüzey gerilimi hissi bundan geliyor.
   ============================================================ */

/* Oranlar: daire çubuğun BÜYÜK ÖLÇÜDE ÜSTÜNDE durmalı, yalnızca
   alt ucu çubuğa değmeli. Aksi halde yuva dairenin altında kalıyor
   ve "erime" hiç görünmüyor. */
const RB = 23;     // daire yarıçapı
const RISE = 20;   // daire merkezinin üst kenardan yüksekliği
const LIFT = RB + RISE + 4; // dairenin üstte ihtiyaç duyduğu boşluk
const BAR_H = 62;  // çubuk yüksekliği
const R = 16;      // çubuk köşe yarıçapı
const S = 26;      // omuz yayı doğal yarıçapı
const INSET = 14;  // sekme seridinin yatay ic boslugu (etiketler sigsin diye daraltildi)

export const MN_BEAD_TOP = LIFT - RISE - RB; // dairenin wrap içindeki üst kenarı

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

/* Teğetlik bağıntısı:  reach² = 2·s·(rb + rise) + rb² − rise²
   İleri yön : reach(s) — omuz yarıçapından erişim
   Ters yön  : s(reach) — dar alanda omuzu toplamak için        */
const reachOf = (s) => {
  const v = 2 * s * (RB + RISE) + RB * RB - RISE * RISE;
  return v > 0 ? Math.sqrt(v) : 0;
};
const sOfReach = (reach) =>
  (reach * reach - RB * RB + RISE * RISE) / (2 * (RB + RISE));

const MIN_REACH = Math.sqrt(Math.max(1, RB * RB - RISE * RISE)) + 0.5;

/** Üst kenarda, verilen merkez etrafında yuva açılmış dock yolu. */
function dockPath(W, cxRaw, lean = 0) {
  const top = LIFT;
  const bottom = LIFT + BAR_H;
  if (!W || W <= 2 * R + 8) return '';

  const cx = clamp(cxRaw, R + MIN_REACH, W - R - MIN_REACH);
  const beadY = top - RISE;

  // Doğal erişim; hız yatmasıyla bir omuz uzar, diğeri toplanır.
  const natural = reachOf(S);
  let reachL = natural * (1 + 0.28 * lean);
  let reachR = natural * (1 - 0.28 * lean);

  // Yuva çubuğun düz üst kenarına sığmalı: köşe yaylarına taşamaz.
  reachL = clamp(reachL, MIN_REACH, Math.max(MIN_REACH, cx - R));
  reachR = clamp(reachR, MIN_REACH, Math.max(MIN_REACH, W - R - cx));

  // Erişimden omuz yarıçapını geri çöz — teğetlik korunur.
  const sL = Math.max(0.5, sOfReach(reachL));
  const sR = Math.max(0.5, sOfReach(reachR));

  const slx = cx - reachL;
  const sly = top - sL;
  const srx = cx + reachR;
  const sry = top - sR;

  // Omuz–daire teğet noktaları (merkezleri birleştiren doğru üzerinde)
  const tl = {
    x: slx + (sL * (cx - slx)) / (sL + RB),
    y: sly + (sL * (beadY - sly)) / (sL + RB),
  };
  const tr = {
    x: srx + (sR * (cx - srx)) / (sR + RB),
    y: sry + (sR * (beadY - sry)) / (sR + RB),
  };

  const n = (v) => v.toFixed(2);

  // Daire yayı TL'den TR'ye saat yönünün TERSİNE (sweep 0), dairenin
  // ALTINDAN geçerek gider. Süpürülen açı 180°'yi aşarsa large-arc 1
  // olmalı; sabit vermek yayı uzun yoldan dolaştırıyordu.
  const aTL = Math.atan2(tl.y - beadY, tl.x - cx);
  const aTR = Math.atan2(tr.y - beadY, tr.x - cx);
  let sweptDeg = ((aTL - aTR) * 180) / Math.PI;
  while (sweptDeg < 0) sweptDeg += 360;
  const largeArc = sweptDeg > 180 ? 1 : 0;

  return [
    `M 0 ${top + R}`,
    `A ${R} ${R} 0 0 1 ${R} ${top}`,
    `L ${n(slx)} ${top}`,
    `A ${n(sL)} ${n(sL)} 0 0 0 ${n(tl.x)} ${n(tl.y)}`,
    `A ${RB} ${RB} 0 ${largeArc} 0 ${n(tr.x)} ${n(tr.y)}`,
    `A ${n(sR)} ${n(sR)} 0 0 0 ${n(srx)} ${top}`,
    `L ${W - R} ${top}`,
    `A ${R} ${R} 0 0 1 ${W} ${top + R}`,
    `L ${W} ${bottom - R}`,
    `A ${R} ${R} 0 0 1 ${W - R} ${bottom}`,
    `L ${R} ${bottom}`,
    `A ${R} ${R} 0 0 1 0 ${bottom - R}`,
    'Z',
  ].join(' ');
}

/* ---------- İkonlar: tek renk, ince çizgi (referanstaki gibi) ---------- */
const Glyph = ({ name }) => {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const paths = {
    home: <path d="M3 10.6 12 3.5l9 7.1M5.6 9.4V20h12.8V9.4" {...p} />,
    folder: <path d="M3 7.4A2 2 0 0 1 5 5.4h3.9l1.8 2H19a2 2 0 0 1 2 2v8.2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" {...p} />,
    calendar: (
      <g {...p}>
        <rect x="3" y="5.2" width="18" height="15.4" rx="2.4" />
        <path d="M3 10h18M8 3.4v3.4M16 3.4v3.4" />
      </g>
    ),
    clipboard: (
      <g {...p}>
        <path d="M9 4.6H7.4a2 2 0 0 0-2 2v12.8a2 2 0 0 0 2 2h9.2a2 2 0 0 0 2-2V6.6a2 2 0 0 0-2-2H15" />
        <rect x="9" y="2.8" width="6" height="3.6" rx="1.4" />
        <path d="M8.8 11.4h6.4M8.8 15h4.4" />
      </g>
    ),
    check: (
      <g {...p}>
        <circle cx="12" cy="12" r="8.6" />
        <path d="M8.4 12.2 10.9 14.8 15.8 9.2" />
      </g>
    ),
    menu: <path d="M4 7.5h16M4 12h16M4 16.5h16" {...p} />,
    wallet: (
      <>
        <path d="M3 7.5A2 2 0 0 1 5 5.5h11a2 2 0 0 1 2 2v1" {...p} />
        <path d="M3 7.5v9A2 2 0 0 0 5 18.5h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H5" {...p} />
        <path d="M17 12.6h.01" {...p} />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      {paths[name] || paths.home}
    </svg>
  );
};

const scrollPageToTop = () => {
  const opts = { top: 0, left: 0, behavior: 'smooth' };
  window.scrollTo(opts);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  ['.app', '.app-content', '.case-file-detail', '.dashboard', '.case-files-page', '.kasa-container'].forEach((sel) => {
    const el = document.querySelector(sel);
    if (el && typeof el.scrollTo === 'function') el.scrollTo(opts);
  });
};

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
  const { user } = useAuth();

  const path = location.pathname;
  const isHome = path === '/' || path === '/dashboard';
  const isActive = (target) => (target === '/dashboard' ? isHome : path.startsWith(target));

  const goHome = () => {
    if (isHome) { scrollPageToTop(); return; }
    navigate('/dashboard');
    window.setTimeout(scrollPageToTop, 50);
  };

  // Kasa sekmesi yalnizca kasaya erisebilen kullanicilarda gorunur.
  const kullaniciId = user?.Id ?? user?.id;
  const kasaVar = kullaniciId === 2 || kullaniciId === 8;

  const items = [
    { key: 'dashboard', label: 'Anasayfa', glyph: 'home', acc: '#c9a24a', onClick: goHome, to: '/dashboard' },
    { key: 'case-files', label: 'Dosyalar', glyph: 'folder', acc: '#f0b429', to: '/case-files' },
    { key: 'calendar', label: 'Takvim', glyph: 'calendar', acc: '#5aa9f2', to: '/calendar' },
    { key: 'job-list', label: 'İş Listesi', glyph: 'clipboard', acc: '#f97f4a', to: '/job-list' },
    { key: 'tasks', label: 'Görevler', glyph: 'check', acc: '#34d399', to: '/tasks' },
    ...(kasaVar ? [{ key: 'kasa', label: 'Kasa', glyph: 'wallet', acc: '#2fbf9a', to: '/kasa' }] : []),
    { key: 'menu', label: 'Menü', glyph: 'menu', acc: '#a3b1c6', onClick: toggleSidebar, to: null },
  ];

  const activeIndex = Math.max(0, items.findIndex((it) => it.to && isActive(it.to)));

  const wrapRef = useRef(null);
  const [w, setW] = useState(0);
  const [cx, setCx] = useState(0);
  const [lean, setLean] = useState(0);
  const anim = useRef(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setW(el.clientWidth);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Sekme merkezleri: şeridin iç boşluğuyla aynı hizada olmalı,
  // yoksa daire ikonun üstüne oturmaz.
  const targetFor = useCallback(
    (i) => (w ? INSET + ((w - 2 * INSET) / items.length) * (i + 0.5) : 0),
    [w, items.length]
  );

  // Hedefe yumuşak geçiş; hız omuzları yatırır.
  useEffect(() => {
    const to = targetFor(activeIndex);
    if (!w) return undefined;
    if (!cx) { setCx(to); return undefined; }
    if (Math.abs(to - cx) < 0.5) return undefined;

    const from = cx;
    const dist = to - from;
    const dur = clamp(260 + Math.abs(dist) * 0.55, 260, 620);
    const t0 = performance.now();
    let prev = from;

    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const t = clamp((now - t0) / dur, 0, 1);
      const val = from + dist * ease(t);
      const v = val - prev;
      prev = val;
      setCx(val);
      setLean(clamp(v / 9, -1, 1));
      if (t < 1) anim.current = requestAnimationFrame(step);
      else { setLean(0); anim.current = null; }
    };

    if (anim.current) cancelAnimationFrame(anim.current);
    anim.current = requestAnimationFrame(step);
    return () => { if (anim.current) cancelAnimationFrame(anim.current); };
    // Bilerek yalnızca activeIndex ve w izleniyor: cx her karede
    // değiştiği için bağımlılığa eklenirse animasyon kendini yeniden
    // başlatır.
  }, [activeIndex, w]); // eslint-disable-line

  const acc = items[activeIndex]?.acc || '#c9a24a';
  const H = LIFT + BAR_H;
  const d = dockPath(w, cx || targetFor(activeIndex), lean);

  return (
    <nav
      className="mn-dock"
      role="navigation"
      aria-label="Alt menü"
      style={{ '--mn-acc': acc }}
    >
      <div className="mn-wrap" ref={wrapRef}>
        <svg className="mn-skin" viewBox={`0 0 ${w || 1} ${H}`} width="100%" height={H} aria-hidden="true">
          <defs>
            <linearGradient id="mnPlate" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#243043" />
              <stop offset="100%" stopColor="#141a25" />
            </linearGradient>
            <linearGradient id="mnRim" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
              <stop offset="60%" stopColor="rgba(255,255,255,0.05)" />
            </linearGradient>
          </defs>
          <path className="mn-fill" d={d} fill="url(#mnPlate)" stroke="url(#mnRim)" strokeWidth="1" />
        </svg>

        {/* Aktif daire — yol ile aynı clamp'e tabi, yoksa hizadan kayar */}
        <span
          className="mn-bead"
          style={{
            top: `${MN_BEAD_TOP}px`,
            width: `${RB * 2}px`,
            height: `${RB * 2}px`,
            transform: `translate(${clamp(cx || targetFor(activeIndex), R + MIN_REACH, Math.max(R + MIN_REACH, (w || 1) - R - MIN_REACH)) - RB}px, 0)`,
          }}
          aria-hidden="true"
        >
          <span className="mn-bead-glyph"><Glyph name={items[activeIndex]?.glyph} /></span>
        </span>

        <div className="mn-tabs" role="tablist">
          {items.map((it, i) => {
            const active = i === activeIndex;
            return (
              <button
                key={it.key}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={it.label}
                className={`mn-tab ${active ? 'active' : ''}`}
                style={{ '--acc': it.acc }}
                onClick={it.onClick || (() => navigate(it.to))}
              >
                <span className="mn-tab-glyph"><Glyph name={it.glyph} /></span>
                <span className="mn-tab-label">{it.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
